import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { ResponseArtifact, ResponseArtifactDTO } from '@dreamai/types';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import api from '../api';
import ResponseArtifactModal from './chat/sidebar/ResponseArtifactModal';
import type { CardSelection } from './chat/sidebar/SpendingApprovalModal';
import { ProjectAppRenderer, SharedAppData } from './SharedProjectAppPage';
import { ReplyToProvider } from '../contexts/ReplyToContext';
import { AgentProvider } from '../contexts/AgentContext';
import { OnboardingModalsProvider } from '../providers/OnboardingModalsProvider';
import './shared-artifact-page.scss';

/**
 * Public page for viewing and responding to a response artifact via share link.
 * Rendered at /artifact/:shareToken — no authentication required.
 * Used for WhatsApp users to interact with artifacts in full-screen.
 */
const SharedArtifactPage: React.FC = () => {
  const { shareToken } = useParams<{ shareToken: string }>();
  const [artifact, setArtifact] = useState<ResponseArtifactDTO | null>(null);
  const [projectApp, setProjectApp] = useState<SharedAppData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const fetchArtifact = async () => {
      if (!shareToken) {
        setError('No share token provided');
        setLoading(false);
        return;
      }

      try {
        const response = await api.get<{
          success: boolean;
          artifact: ResponseArtifactDTO;
          projectApp?: SharedAppData;
          error?: string;
        }>(`/public/artifacts/${shareToken}`);

        if (response.data.success && response.data.artifact) {
          setArtifact(response.data.artifact);
          if (response.data.projectApp) {
            setProjectApp(response.data.projectApp);
          }
        } else {
          setError(response.data.error || 'Failed to load artifact');
        }
      } catch (err: any) {
        if (err.response?.status === 410) {
          setError('This link has expired. Please ask for a new one.');
        } else if (err.response?.status === 404) {
          setError('This link is invalid or the item no longer exists.');
        } else {
          setError('Something went wrong. Please try again later.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchArtifact();
  }, [shareToken]);

  const handleSubmitResponse = useCallback(
    async (
      art: ResponseArtifactDTO,
      response: any,
      mandateOptions?: {
        createMandate: boolean;
        mandateItems: Array<{
          actionLine: string;
          descriptionLine: string;
          price?: number;
          price_currency?: string;
          isDeposit?: boolean;
        }>;
      },
      cardSelection?: CardSelection,
    ) => {
      if (!shareToken || !artifact) return;

      try {
        await api.patch(`/response-artifacts/${art.id}`, {
          artifactId: art.id,
          status: 'actioned',
          userResponse: response,
          shareToken,
          ...(mandateOptions?.createMandate && {
            create_mandate: true,
            mandate_items: mandateOptions.mandateItems,
          }),
          ...(cardSelection && { card_selection: cardSelection }),
        });
        setArtifact((prev) =>
          prev ? { ...prev, status: 'actioned', userResponse: response } : prev,
        );
        setSubmitted(true);
      } catch (err: any) {
        if (err.response?.status === 410) {
          setError('This link has expired.');
        } else if (err.response?.status === 409) {
          setError('This has already been responded to.');
        } else {
          setError('Failed to submit response. Please try again.');
        }
      }
    },
    [shareToken, artifact],
  );

  if (loading) {
    return (
      <div className='shared-artifact-page'>
        <div className='shared-artifact-loading'>
          <div className='loading-spinner' />
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='shared-artifact-page'>
        <div className='shared-artifact-error'>
          <h2>Unable to load</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className='shared-artifact-page'>
        <div className='shared-artifact-success'>
          <div className='success-icon'>✓</div>
          <h2>Response submitted</h2>
          <p>Your response has been recorded. You can close this page.</p>
        </div>
      </div>
    );
  }

  if (!artifact) return null;

  const isActioned = artifact.status === 'actioned';

  // Information dump artifacts render as a full-page document (same as /shared/ pages)
  if (artifact.artifactType === 'information_dump') {
    if (projectApp && projectApp.blocks.length > 0) {
      return <ProjectAppRenderer appData={projectApp} />;
    }

    // Fallback: legacy markdown content
    const content = (artifact as any).content || artifact.overview;
    return (
      <div className='shared-artifact-page'>
        <div className='shared-artifact-document'>
          {artifact.title && <h1>{artifact.title}</h1>}
          {content ? (
            <div className='document-content'>
              <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
                {content}
              </ReactMarkdown>
            </div>
          ) : (
            <p className='no-content'>No content available.</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className='shared-artifact-page'>
      <div className='shared-artifact-container'>
        <AgentProvider>
          <OnboardingModalsProvider>
            <ReplyToProvider>
              <ResponseArtifactModal
                show={true}
                onClose={() => {}}
                artifacts={[artifact]}
                initialIndex={0}
                onSubmitResponse={handleSubmitResponse}
                readOnly={isActioned}
              />
            </ReplyToProvider>
          </OnboardingModalsProvider>
        </AgentProvider>
      </div>
    </div>
  );
};

export default SharedArtifactPage;
